/* ============================================================
   MOVASANA — KAUFLEISTE (Produktseiten)

   Die Leiste besitzt bewusst KEIN eigenes Kaufformular. Sie zeigt an, und
   ihr Knopf loest das eine bestehende <product-form> der Seite aus. Damit
   kann sie nicht die falsche Variante kaufen, und Dawns komplette
   Warenkorb-Logik (AJAX, Fehlermeldungen, Drawer) wird wiederverwendet
   statt nachgebaut.
   ============================================================ */

class MovasanaStickyCart extends HTMLElement {
  connectedCallback() {
    const daten = this.querySelector('[data-varianten]');
    try {
      this.varianten = JSON.parse(daten.textContent);
    } catch (e) {
      // Lieber gar keine Leiste als eine mit falschen Preisen.
      this.remove();
      return;
    }

    this.bild = this.querySelector('.mv-cart__bild');
    this.variantenName = this.querySelector('.mv-cart__variante');
    this.betrag = this.querySelector('.mv-cart__betrag');
    this.ersparnis = this.querySelector('.mv-cart__ersparnis');
    this.knopf = this.querySelector('.mv-cart__knopf');
    this.meldung = this.querySelector('.mv-cart__meldung');

    // Das EINE Kaufformular der Seite.
    this.formular = document.querySelector('product-form form[data-type="add-to-cart-form"]');
    this.hauptKnopf = this.formular ? this.formular.querySelector('[name="add"]') : null;

    // Ohne Kaufformular hat die Leiste keinen Zweck.
    if (!this.formular) {
      this.remove();
      return;
    }

    this.knopf.addEventListener('click', () => this.kaufen());

    this.beobachten();
    this.aufVariantenwechselHoeren();
    this.aufWarenkorbHoeren();
    this.demHauptknopfFolgen();
    this.demCookieBannerAusweichen();
  }

  disconnectedCallback() {
    if (this.beobachter) this.beobachter.disconnect();
    if (this.bannerBeobachter) this.bannerBeobachter.disconnect();
    if (this.knopfBeobachter) this.knopfBeobachter.disconnect();
    clearTimeout(this.notbremse);
    if (this.abmelden) this.abmelden();
    if (this.abmeldenWarenkorb) this.abmeldenWarenkorb.forEach((ab) => ab());
  }

  /* Der Cookie-Banner ist unten fixiert und liegt ueber der Kaufleiste. Fuer
     Erstbesucher — also genau die, die man konvertieren will — waere der
     Kaufknopf damit unerreichbar, bis sie Cookies akzeptiert haben.

     Den Banner nach unten zu druecken ist keine Option (er muss bedienbar
     bleiben). Also weicht die Leiste ihm aus, solange er da ist, und rutscht
     zurueck, sobald er verschwindet. */
  demCookieBannerAusweichen() {
    /* Alles, was unten fixiert ist und die Leiste verdecken koennte.
       .cc-window   = Cookie-Banner
       #satcb_bar   = App "Sticky Add To Cart Bar"
       #pop-convert-app = Popup-App */
    const HINDERNISSE = '.cc-window, #satcb_bar, #pop-convert-app';

    /* ACHTUNG: `offsetParent !== null` ist als Sichtbarkeitspruefung hier FALSCH.
       Bei position: fixed liefert offsetParent IMMER null — die Pruefung haelt
       den Banner dann fuer unsichtbar, das Ausweichen bleibt aus, und der
       Kaufknopf liegt unter dem Cookie-Banner begraben. */
    const sichtbar = (el) => {
      if (!el) return false;
      const cs = getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden' || Number(cs.opacity) === 0) return false;
      return el.getBoundingClientRect().height > 0;
    };

    const anpassen = () => {
      let hoechstes = 0;
      document.querySelectorAll(HINDERNISSE).forEach((el) => {
        if (!sichtbar(el)) return;
        const r = el.getBoundingClientRect();
        // Nur was wirklich unten klebt, nicht irgendein Element weiter oben.
        if (r.bottom < window.innerHeight - 8) return;
        hoechstes = Math.max(hoechstes, Math.round(r.height));
      });
      this.style.setProperty('--mv-cart-ausweichen', `${hoechstes ? hoechstes + 8 : 0}px`);
    };

    anpassen();

    // Banner werden per JS eingehaengt und wieder entfernt — Aenderungen ueberall
    // im Body beobachten, nicht nur auf oberster Ebene.
    this.bannerBeobachter = new MutationObserver(anpassen);
    this.bannerBeobachter.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
    window.addEventListener('resize', anpassen, { passive: true });

    // Sie erscheinen oft erst kurz nach dem Laden.
    setTimeout(anpassen, 800);
    setTimeout(anpassen, 2500);
  }

  /* Erst zeigen, wenn der eigentliche Kaufknopf aus dem Bild gescrollt ist —
     solange er sichtbar ist, waere die Leiste eine reine Dopplung. */
  beobachten() {
    if (!this.hauptKnopf || !('IntersectionObserver' in window)) {
      this.setAttribute('data-sichtbar', 'true');
      return;
    }

    this.beobachter = new IntersectionObserver(
      (eintraege) => {
        this.setAttribute('data-sichtbar', String(!eintraege[0].isIntersecting));
      },
      { rootMargin: '0px 0px -40px 0px' }
    );
    this.beobachter.observe(this.hauptKnopf);
  }

  /* Dawn sendet bei jedem Variantenwechsel das komplette Varianten-Objekt
     (VariantRadios erbt von VariantSelects, beide publizieren).

     ACHTUNG, subtile Falle: constants.js deklariert `const PUB_SUB_EVENTS`.
     Ein top-level `const` landet — anders als eine `function` — NICHT auf
     `window`. Eine Pruefung auf `window.PUB_SUB_EVENTS` ergibt undefined, das
     Abo wird nie abgeschlossen, und die Leiste zeigt still fuer immer den Preis
     der ERSTEN Variante an. Kein Fehler, kein Hinweis. Deshalb der blanke
     Bezeichner, abgesichert ueber typeof. */
  aufVariantenwechselHoeren() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    this.abmelden = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variante = event && event.data && event.data.variant;
      if (variante) this.zeigeVariante(variante.id);
    });
  }

  zeigeVariante(id) {
    const v = this.varianten.find((x) => x.id === Number(id));
    if (!v) return;

    this.dataset.variante = v.id;

    if (this.bild && v.bild) this.bild.src = v.bild;
    if (this.variantenName) this.variantenName.textContent = v.titel === 'Default Title' ? '' : v.titel;
    if (this.betrag) this.betrag.textContent = v.preis;
    if (this.ersparnis) this.ersparnis.textContent = v.ersparnis ? `${v.ersparnis} gespart` : '';

    this.knopf.disabled = !v.verfuegbar;
    this.knopf.textContent = v.verfuegbar
      ? this.knopf.dataset.label || 'In den Warenkorb'
      : (window.variantStrings && window.variantStrings.soldOut) || 'Ausverkauft';
  }

  /* Erfolg und Absage kommen als dieselben Ereignisse an, die auch der
     Warenkorb-Drawer hoert. Ohne dieses Abo bliebe die Leiste nach einem Klick
     fuer immer im Ladezustand stehen. */
  aufWarenkorbHoeren() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    this.abmeldenWarenkorb = [
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === 'product-form') this.zustandZuruecksetzen();
      }),
      subscribe(PUB_SUB_EVENTS.cartError, (event) => {
        this.zustandZuruecksetzen();
        const text =
          (event && event.message) ||
          (window.variantStrings && window.variantStrings.addToCartNetworkError) ||
          '';
        this.zeigeMeldung(text);
      }),
    ];
  }

  /* Der Hauptknopf ist die Wahrheit ueber Kaufbarkeit: Dawn sperrt ihn beim
     Variantenwechsel und gibt ihn erst frei, wenn Preis und Verfuegbarkeit
     bestaetigt sind. Schlaegt der Abruf fehl, bleibt er gesperrt — die Leiste
     muss dann dasselbe tun, sonst kauft man ueber sie eine unbestaetigte
     Variante. */
  demHauptknopfFolgen() {
    if (!this.hauptKnopf || !('MutationObserver' in window)) return;

    const spiegeln = () => {
      const gesperrt =
        this.hauptKnopf.hasAttribute('disabled') ||
        this.hauptKnopf.getAttribute('aria-disabled') === 'true';
      if (gesperrt !== this.knopf.disabled) this.knopf.disabled = gesperrt;
    };

    this.knopfBeobachter = new MutationObserver(spiegeln);
    this.knopfBeobachter.observe(this.hauptKnopf, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
    });
  }

  zeigeMeldung(text) {
    if (!this.meldung) return;
    this.meldung.textContent = text;
    this.meldung.hidden = !text;
  }

  zustandZuruecksetzen() {
    clearTimeout(this.notbremse);
    this.knopf.classList.remove('mv-cart__knopf--laedt');
    this.knopf.removeAttribute('aria-busy');
    if (this.knopf.dataset.textVorher) {
      this.knopf.textContent = this.knopf.dataset.textVorher;
      delete this.knopf.dataset.textVorher;
    }
    this.knopf.disabled = this.hauptKnopf
      ? this.hauptKnopf.hasAttribute('disabled') ||
        this.hauptKnopf.getAttribute('aria-disabled') === 'true'
      : false;
  }

  kaufen() {
    if (this.knopf.disabled) return;

    this.zeigeMeldung('');
    this.knopf.dataset.textVorher = this.knopf.textContent;
    this.knopf.textContent =
      (window.variantStrings && window.variantStrings.addingToCart) || this.knopf.textContent;
    this.knopf.classList.add('mv-cart__knopf--laedt');
    this.knopf.setAttribute('aria-busy', 'true');
    this.knopf.disabled = true;

    /* Notbremse: bleibt jedes Ereignis aus — abgebrochener Aufruf, blockiertes
       Skript — waere die Leiste sonst dauerhaft tot. Lieber ein Knopf, der
       wieder geht, als einer, der nie mehr reagiert. */
    clearTimeout(this.notbremse);
    this.notbremse = setTimeout(() => {
      if (this.knopf.getAttribute('aria-busy') !== 'true') return;
      this.zustandZuruecksetzen();
      this.zeigeMeldung(
        (window.variantStrings && window.variantStrings.addToCartNetworkError) || ''
      );
    }, 12000);

    if (typeof this.formular.requestSubmit === 'function') {
      // requestSubmit loest das submit-Ereignis aus -> Dawns product-form.js
      // uebernimmt AJAX, Fehlerbehandlung und Warenkorb-Drawer.
      this.formular.requestSubmit();
    } else if (this.hauptKnopf) {
      this.hauptKnopf.click();
    }
  }
}

if (!customElements.get('movasana-sticky-cart')) {
  customElements.define('movasana-sticky-cart', MovasanaStickyCart);
}
